import { useState, useEffect, useMemo } from "react";
import {
  ActionButton,
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Text,
  View,
} from "@adobe/react-spectrum";
import { connect, useSelector } from "react-redux";
import Emitter from "../../../emitter";
import { caseAnswer } from "../../../controller/cases";
import { API_CASE_REJECT, CASE_STATES } from "../../../constants/constants";
import { EnvelopePlusIcon } from "../../common/Icons";
import { getInitialsAndColor } from "../../../utils";
import { RootState } from "../../../store/storeConfig";
import { BusinessAttributes } from "../../../types/agent.type";
import { b64DecodeUnicode, formatLabel } from "../../../utils/utils";

function mapStateToProps(state: any) {
  return {
    cases: state.cases,
  };
}

const CaseAcceptDialog = (props: any) => {
  const [isOpen, setOpen] = useState(false);
  const agent = useSelector((state: RootState) => state.agent);

  const indCase = props.cases.cases.find(
    (c: any) => c.self.state === "RINGING"
  );
  let caseAttributes = indCase?.customer?.attributes;
  let agentBusinessAttributes = agent?.businessAttributes || [];

  useEffect(() => {
    if (
      indCase &&
      indCase?.id &&
      indCase?.self?.state === CASE_STATES.RINGING
    ) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [indCase]);

  const { initials, avatarColor } = useMemo(() => {
    const first = caseAttributes?.FIRSTNAME?.[0] || "";
    const last = caseAttributes?.LASTNAME?.[0] || "";
    const initials = `${first} ${last}`.trim();
    return {
      initials,
      avatarColor: getInitialsAndColor(initials, "color"),
    };
  }, [caseAttributes]);

  const rejectCaseHandler = () => {
    const indCase = props.cases.cases.find(
      (c: any) => c.self.state === "RINGING"
    );
    console.log("rejectCaseHandler", indCase);
    // caseAnswer(indCase.id, indCase.self.id);
    console.log(Emitter.listenerCount(API_CASE_REJECT));
    Emitter.emit(API_CASE_REJECT, {
      body: {
        participantId: indCase?.self?.id,
        conversationId: indCase.id,
        state: "disconnected",
      },
    });
    setOpen(false);
  };

  const pickupCaseHandler = () => {
    const indCase = props.cases.cases.find(
      (c: any) => c.self.state === "RINGING"
    );
    caseAnswer(indCase.id, indCase.self.id);
    setOpen(false);
  };

  // 1. use filter to compare agent attributes with call attributes. Get matching attributes as output.
  let matchingAttributes = agentBusinessAttributes.filter(function (
    entry: BusinessAttributes
  ) {
    if (
      (entry.attributeName.toLowerCase() === "notes"||
      entry.attributeName.toLowerCase() === "caseid" ||
      entry.attributeName.toLowerCase() === "case_description_base64" ||
      entry.attributeName.toLowerCase() === "firstname" ||
      entry.attributeName.toLowerCase() === "lastname" ||
      entry.attributeName.toLowerCase() === "email" ||
      entry.attributeName.toLowerCase() === "case_title"
    ) && caseAttributes?.hasOwnProperty(entry.attributeName)
    ) {
      return false;
    }

    return caseAttributes?.hasOwnProperty(entry.attributeName);
  });

  let finalArray = [];
  for (let i = 0; i < matchingAttributes?.length; i += 3) {
    const attributes = [matchingAttributes[i], matchingAttributes[i + 1]];

    finalArray.push(
      <View
        key={`attribute-group-${i}`}
        UNSAFE_className="call-incoming-attributes"
      >
        <Flex direction="row" wrap="wrap" gap={40}>
          {attributes.map((attribute, index) =>
            attribute ? (
              <Flex
                direction="column"
                key={`attribute-${i + index}`}
                UNSAFE_style={{ flex: 1, minWidth: "45%", maxWidth: "45%" }}
              >
                <Text
                  UNSAFE_className="call-content-heading"
                  UNSAFE_style={{
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {formatLabel(attribute.attributeName)}
                </Text>

                <Text
                  UNSAFE_className="call-content-descritpion"
                  UNSAFE_style={{
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {caseAttributes[attribute.attributeName]}
                </Text>
              </Flex>
            ) : null
          )}
        </Flex>
      </View>
    );
  }

  return (
    <>
      {isOpen && (
        <DialogTrigger type="modal" mobileType="modal" defaultOpen={true}>
          <ActionButton isHidden isQuiet></ActionButton>
          {() => (
            <Dialog size={"S"} UNSAFE_className="call-incoming-dialog">
              <Heading level={4}>
                <Flex direction={"row"} gap={"size-100"} alignItems={"center"}>
                  <Text UNSAFE_className="call-heading-icon">
                    <EnvelopePlusIcon />
                  </Text>
                  <Text UNSAFE_className="call-heading-text">
                    New incoming case
                  </Text>
                </Flex>
              </Heading>
              <Divider
                marginBottom={"size-0"}
                UNSAFE_className="call-incoming-divider"
              />
              <Content maxWidth={"100vw"} marginTop={"15px"}>
                <Content>
                  <Flex direction={"column"} gap={"size-200"} width={"100%"}>
                    <Flex direction={"row"} width={"100%"} gap={40}>
                      <Flex direction={"column"} width={"45%"}>
                        <Flex direction="row" alignItems="center">
                          <View UNSAFE_className="call-history-avatar">
                            <Flex
                              justifyContent="center"
                              alignItems="center"
                              UNSAFE_className="agent-avatar"
                              UNSAFE_style={{
                                backgroundColor: avatarColor as string,
                                borderRadius: "50%",
                              }}
                            >
                              <Text UNSAFE_className="call-monitor-agent-text">
                                {initials}
                              </Text>
                            </Flex>
                          </View>
                          <Flex direction="column" marginStart="12px">
                            <Text
                              margin="0"
                              UNSAFE_className="case-info-subheading"
                            >
                              {caseAttributes?.FIRSTNAME
                                ? `${caseAttributes?.FIRSTNAME} ${
                                    caseAttributes.LASTNAME ?? ""
                                  }`
                                : "NA"}
                            </Text>
                            <Text UNSAFE_className="case-detail-item-info">
                              {caseAttributes?.EMAIL || "-"}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                      <Flex direction={"column"} width={"45%"}>
                        <Text
                          UNSAFE_className="call-content-heading"
                          UNSAFE_style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          Case ID
                        </Text>

                        <Text
                          UNSAFE_className="call-content-descritpion"
                          UNSAFE_style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {caseAttributes?.CASEID}
                        </Text>
                      </Flex>
                    </Flex>
                    <View>{finalArray}</View>
                    <Flex direction={"column"} width={"100%"}>
                      <Text
                        UNSAFE_className="call-content-heading"
                        UNSAFE_style={{
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        Case Title
                      </Text>

                      <Text
                        UNSAFE_className="call-content-descritpion"
                        UNSAFE_style={{
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {caseAttributes?.CASE_TITLE}
                      </Text>
                    </Flex>
                    {caseAttributes?.CASE_DESCRIPTION_BASE64?.length > 0 && (
                      <Flex direction={"column"} width={"100%"}>
                        <Text
                          UNSAFE_className="call-content-heading"
                          UNSAFE_style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          Description
                        </Text>
                        <Text
                          UNSAFE_className="call-content-descritpion"
                          UNSAFE_style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {(() => {
                            const base64 =
                              caseAttributes?.CASE_DESCRIPTION_BASE64 ?? "";
                            if (base64.length === 0) return "NA";
                            try {
                              const decodedText = b64DecodeUnicode(base64);
                              return decodedText.length > 300 
                                ? `${decodedText.substring(0, 300)}...` 
                                : decodedText;
                            } catch (e) {
                              return "NA";
                            }
                          })()}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                </Content>
              </Content>
              <ButtonGroup>
                <Button
                  UNSAFE_className="call-reject-btn"
                  variant="negative"
                  onPress={rejectCaseHandler}
                  autoFocus={false}
                >
                  <span className="reject-btn-icon">
                    {/* <CallReject /> */}
                  </span>
                  <Text UNSAFE_style={{ display: "contents" }}>Reject</Text>
                </Button>
                <Button
                  variant="accent"
                  UNSAFE_className="call-accept-btn"
                  onPress={pickupCaseHandler}
                  autoFocus={false}
                >
                  <span className="calling-btn-icon">
                    {/* <CallAccept /> */}
                  </span>
                  <Text UNSAFE_style={{ display: "contents" }}>Accept</Text>
                </Button>
              </ButtonGroup>
            </Dialog>
          )}
        </DialogTrigger>
      )}
    </>
  );
};
export default connect(mapStateToProps)(CaseAcceptDialog);
